import { useRef, useEffect } from 'react'

/**
 * Custom hook for managing AbortController instances
 * Provides unified interface for request cancellation management
 */
export const useAbortController = () => {
  const abortControllerRef = useRef<AbortController | null>(null)

  /**
   * Creates new AbortController, automatically aborting previous one if exists
   * @returns AbortSignal for the new controller
   */
  const createController = (): AbortSignal => {
    // Abort previous controller if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Create new controller
    abortControllerRef.current = new AbortController()
    return abortControllerRef.current.signal
  }

  /**
   * Cleanup method to reset controller reference
   * Should be called after request completion
   */
  const cleanup = (): void => {
    abortControllerRef.current = null
  }

  /**
   * Abort current controller if exists
   */
  const abort = (): void => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      cleanup()
    }
  }

  /**
   * Check if controller exists and is not aborted
   */
  const isActive = (): boolean => {
    return abortControllerRef.current !== null && !abortControllerRef.current.signal.aborted
  }

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  return {
    createController,
    cleanup,
    abort,
    isActive
  }
}